import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import CategoriesSelect from "../components/Categories/CategoriesSelect";

const toSlug = (text = "") =>
  String(text)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

const normalizeSections = (data) => {
  if (!Array.isArray(data)) return [];

  return data.map((section) => ({
    id: section.id,
    name: section.name || "",
    sequential_code: section.sequential_code || "",
    image: section.image || null,
    categories: Array.isArray(section.categories)
      ? section.categories.map((category) => ({
          id: category.id,
          name: category.name || "",
          sequential_code: category.sequential_code || "",
        }))
      : [],
  }));
};

const CategoriesPage = () => {
  const navigate = useNavigate();
  const { sectionSlug, categorySlug } = useParams();

  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const normalizedSectionSlug = useMemo(
    () => toSlug(sectionSlug),
    [sectionSlug]
  );

  const normalizedCategorySlug = useMemo(
    () => toSlug(categorySlug),
    [categorySlug]
  );

  useEffect(() => {
    document.title = "Tienda | CMSOLTEC";
  }, []);

  useEffect(() => {
    let active = true;

    const fetchSections = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await fetch("/categorias/public/estructura/", {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("No se pudo cargar la estructura de categorías.");
        }

        const data = await response.json();
        const normalizedData = normalizeSections(data);

        if (!active) return;

        setSections(normalizedData);
      } catch (requestError) {
        if (!active) return;

        setSections([]);
        setSelectedSection(null);
        setSelectedCategory(null);
        setError(
          requestError?.message ||
            "No se pudo cargar la información de la tienda."
        );
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchSections();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!sectionSlug || !categorySlug) {
      setSelectedSection(null);
      setSelectedCategory(null);
      return;
    }

    const section = sections.find(
      (item) => toSlug(item.name) === normalizedSectionSlug
    );

    const category = section?.categories.find(
      (item) => toSlug(item.name) === normalizedCategorySlug
    );

    if (section && category) {
      setSelectedSection(section);
      setSelectedCategory(category);
      return;
    }

    setSelectedSection(null);
    setSelectedCategory(null);
  }, [
    sections,
    sectionSlug,
    categorySlug,
    normalizedSectionSlug,
    normalizedCategorySlug,
  ]);

  const handleCategoryClick = (section, category) => {
    navigate(
      `/categorias/${toSlug(section.name)}/${toSlug(category.name)}`
    );
  };

  const handleClearCategory = () => {
    setSelectedSection(null);
    setSelectedCategory(null);
    navigate("/categorias");
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#f5f5f5",
        }}
      >
        <CircularProgress size={34} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#f5f5f5",
          px: 2,
        }}
      >
        <Typography
          sx={{
            color: "#b42318",
            fontSize: "1rem",
            fontWeight: 700,
            textAlign: "center",
          }}
        >
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f5f5f5",
        px: {
          xs: 1.5,
          sm: 2.5,
          md: 4,
        },
        py: {
          xs: 2,
          sm: 3,
          md: 4,
        },
      }}
    >
      {selectedSection && selectedCategory ? (
        <CategoriesSelect
          section={selectedSection}
          category={selectedCategory}
          setSelectedCategory={(category) => {
            if (!category) {
              handleClearCategory();
              return;
            }

            setSelectedCategory(category);
          }}
        />
      ) : sections.length > 0 ? (
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 1400,
            mx: "auto",
            p: {
              xs: 2,
              sm: 3,
              md: 4,
            },
            borderRadius: 2,
            bgcolor: "#ffffff",
            border: "1px solid #e5e7eb",
          }}
        >
          {sections.map((section, sectionIndex) => (
            <motion.div
              key={section.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.35,
                delay: sectionIndex * 0.08,
              }}
            >
              <Box sx={{ mb: sectionIndex < sections.length - 1 ? 4 : 0 }}>
                <Grid container spacing={2} justifyContent="center">
                  {section.image && (
                    <Grid
                      item
                      xs={12}
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          minHeight: {
                            xs: 170,
                            sm: 210,
                            md: 250,
                          },
                          maxHeight: {
                            xs: 320,
                            sm: 380,
                            md: 430,
                          },
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          component="img"
                          src={section.image}
                          alt={section.name}
                          loading="lazy"
                          sx={{
                            display: "block",
                            width: "100%",
                            height: "100%",
                            maxWidth: {
                              xs: "100%",
                              sm: 720,
                              md: 900,
                            },
                            maxHeight: {
                              xs: 320,
                              sm: 380,
                              md: 430,
                            },
                            objectFit: "contain",
                          }}
                        />
                      </Box>
                    </Grid>
                  )}

                  <Grid
                    item
                    xs={12}
                    sx={{
                      textAlign: "center",
                      mt: section.image ? 1 : 0,
                    }}
                  >
                    <Typography
                      component="h2"
                      sx={{
                        color: "#111827",
                        fontSize: {
                          xs: "1.35rem",
                          sm: "1.55rem",
                          md: "1.8rem",
                        },
                        fontWeight: 800,
                        lineHeight: 1.2,
                        mb: 2,
                      }}
                    >
                      {section.name}
                    </Typography>

                    <Typography
                      sx={{
                        color: "#0A3D91",
                        fontSize: {
                          xs: "0.9rem",
                          sm: "1rem",
                        },
                        fontWeight: 800,
                        letterSpacing: "0.08em",
                        mb: 2,
                      }}
                    >
                      CATEGORÍAS
                    </Typography>

                    {section.categories.length > 0 ? (
                      <Grid container spacing={1.25} justifyContent="center">
                        {section.categories.map((category) => (
                          <Grid
                            item
                            xs={6}
                            sm={4}
                            md={3}
                            lg={2.4}
                            key={category.id}
                          >
                            <Box
                              component="button"
                              type="button"
                              onClick={() =>
                                handleCategoryClick(section, category)
                              }
                              sx={{
                                width: "100%",
                                minHeight: 44,
                                px: 1,
                                py: 1,
                                border: 0,
                                borderRadius: 1,
                                bgcolor: "transparent",
                                color: "#232323",
                                cursor: "pointer",
                                fontFamily: "inherit",
                                fontSize: {
                                  xs: "0.9rem",
                                  sm: "1rem",
                                  md: "1.05rem",
                                },
                                fontWeight: 600,
                                transition:
                                  "color 0.2s ease, background-color 0.2s ease",
                                "&:hover": {
                                  color: "#0D6EFD",
                                  bgcolor: "#f3f7ff",
                                },
                                "&:focus-visible": {
                                  outline: "2px solid #0D6EFD",
                                  outlineOffset: 2,
                                },
                              }}
                            >
                              {category.name}
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    ) : (
                      <Typography
                        sx={{
                          color: "#6b7280",
                          fontSize: "0.95rem",
                        }}
                      >
                        Esta sección no tiene categorías disponibles.
                      </Typography>
                    )}
                  </Grid>
                </Grid>

                {sectionIndex < sections.length - 1 && (
                  <Divider
                    sx={{
                      mt: 4,
                      borderColor: "#e5e7eb",
                    }}
                  />
                )}
              </Box>
            </motion.div>
          ))}
        </Paper>
      ) : (
        <Box
          sx={{
            minHeight: "50vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            sx={{
              color: "#6b7280",
              fontSize: "1rem",
              fontWeight: 600,
              textAlign: "center",
            }}
          >
            No hay secciones disponibles.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default CategoriesPage;
